/**
 * inbound.ts — 자재 입고(material_inbound) 도메인 타입.
 *
 * Firestore: "material_inbound" — 자동 ID. 1행 = 1건 입고 (1자재 + 1업체 + 1수량).
 *
 * 자재 코드 단일화(2026-05-08) 후 — material_categories가 단일 소스.
 * 입고 dropdown은 useMaterialCategories() + category 필드로 직접 그룹화.
 *
 * 단위는 material_categories에 없어 category로부터 추론 (UNIT_BY_CATEGORY).
 * 기타(FUEL, TRANSPORT 등) 카테고리는 자재마다 단위가 달라 빈 문자열로 두고
 * 호출자가 명시적으로 채움.
 */

import type { MaterialCategoryKind } from "./material";
import type { LineCode } from "./material_mapping";

/** category → 입고 단위 추론. 기타는 빈 문자열 */
export const UNIT_BY_CATEGORY: Readonly<Record<MaterialCategoryKind, string>> = {
  결합제:   "톤",
  굵은골재: "㎥",
  잔골재:   "㎥",
  혼화제:   "L",
  기타:     "",
};

export interface MaterialInbound {
  /** Firestore 문서 ID */
  id: string;
  /** 입고일 YYYY-MM-DD */
  date: string;
  /** 연월 YYYY-MM (쿼리용 파생 필드, repo에서 자동 채움) */
  ym: string;
  /** 자재 코드 — material_categories.code */
  mat_code: string;
  /** 자재명 (캐시) */
  mat_name: string;
  /** 업체명 (자유 입력) */
  supplier: string;
  /**
   * 실제로 부은 라인(사일로/탱크). 자재가 여러 라인에 매핑될 때 어디에 넣었는지의 사실값.
   *
   * 없으면(과거분·후보 1개) 입고일 시점 매핑으로 추론 — domain/inbound_line.ts.
   * 매핑(line_codes)은 "들어갈 수 있는 후보", 이 필드는 "이번에 실제로 넣은 곳".
   */
  line_code?: LineCode;
  /** 수량 */
  quantity: number;
  /** 단위 (category 기반 추론값 캐시) */
  unit: string;
  /** 입력식 — 산술식으로 입력한 경우 원본 (예: "17*5"). 단순 숫자면 없음 */
  qty_expr?: string;
  /** 자재비 (원/단위) */
  unit_price: number;
  /** 운반비 (원/단위) */
  transport_fee: number;
  /** 사일로 — 마이그레이션 이전 S/C 데이터 호환용. 신규 입력 시 빈 문자열 */
  silo: string;
  /** 시멘트 종류 — 마이그레이션 이전 S/C 데이터 호환용. 신규 입력 시 빈 문자열 */
  cement_type: string;
  /** 비고 */
  memo: string;
  /** 입력자 UID (저장 시 자동) */
  user_id?: string;
  created_at?: Date;
  updated_at?: Date;
}

/** 신규/편집 폼 입력 — 자동 채움 필드(ym/user_id/timestamps) 제외. mat_name/unit은 호출자가 전달 */
export type MaterialInboundDraft = Omit<
  MaterialInbound,
  "id" | "ym" | "user_id" | "created_at" | "updated_at"
> & {
  /** 편집 모드일 때만 채움 */
  id?: string;
};

/** 한 행 합계 = (단가 + 운반비) × 수량 */
export function inboundTotal(
  p: Pick<MaterialInbound, "unit_price" | "transport_fee" | "quantity">,
): number {
  return (Number(p.unit_price ?? 0) + Number(p.transport_fee ?? 0)) * Number(p.quantity ?? 0);
}

/** YYYY-MM-DD → YYYY-MM */
export function dateToYm(date: string): string {
  return (date ?? "").slice(0, 7);
}

/** 오늘 YYYY-MM-DD */
export function todayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

/** 오늘 YYYY-MM */
export function todayYm(): string {
  return new Date().toISOString().slice(0, 7);
}
